/**
 * VALIDATION BATTERY
 *
 * "Is this real, defensible, and safe to act on?"
 *
 * This is not analytics. This is epistemology encoded in software.
 *
 * Validate is a battery of falsification and coherence tests applied
 * to the field to determine whether a signal is real, persistent,
 * ethically actionable, and causally meaningful — before it is
 * allowed to influence design or spend.
 *
 * Ten tests. Each produces a categorical state, not a score.
 * Rules-based, not ML-scored. No numbers.
 * States only.
 */

import { CATEGORIES, type CategoryDefinition } from './synthetic';

// ─── THE TEN DIMENSIONS ─────────────────────────────────────────

/** 1. Signal Convergence Testing
 * Multiple independent signal classes, not just volume. */
export type ConvergenceState = 'convergent' | 'single-source' | 'observational';

/** 2. Temporal Persistence Analysis
 * Does it survive beyond novelty? */
export type PersistenceState = 'persistent' | 'volatile' | 'peaking' | 'decaying';

/** 3. Cross-Geographic Coherence
 * Local? Regional? National? Global? */
export type GeographicState = 'diffusing' | 'contained' | 'fragmented' | 'region-specific';

/** 4. Semantic Stability Check
 * Is the meaning consistent, or drifting? */
export type SemanticState = 'stable' | 'evolving' | 'fracturing';

/** 5. Noise-to-Signal Ratio Filter
 * Structured interest vs background chatter. */
export type NoiseState = 'structured' | 'noisy' | 'speculative';

/** 6. Intent Depth Classification
 * Why people engage, not just that they do. */
export type IntentState = 'low-commitment' | 'decision-forming' | 'identity-anchored';

/** 7. Causal Precursor Detection
 * Leading, lagging, or downstream? */
export type CausalState = 'leading' | 'concurrent' | 'reactive';

/** 8. Consent & Ethical Safety Check
 * Public, aggregated, non-extractive, actionable without violating trust. */
export type EthicsState = 'safe-to-activate' | 'observe-only' | 'restricted';

/** 9. False Positive Stress Test
 * Actively tries to disprove the signal. */
export type FalsePositiveState = 'robust' | 'unproven' | 'likely-false-positive';

/** 10. Claim Readiness State
 * Synthesizes all checks into a non-numeric readiness state. */
export type ReadinessState = 'too-early' | 'forming' | 'defensible' | 'ready-to-design-against';


// ─── THE BATTERY ─────────────────────────────────────────────────

export interface ValidationBattery {
  convergence: ConvergenceState;
  persistence: PersistenceState;
  geographic: GeographicState;
  semantic: SemanticState;
  noise: NoiseState;
  intent: IntentState;
  causal: CausalState;
  ethics: EthicsState;
  falsePositive: FalsePositiveState;
  readiness: ReadinessState;
}

/** Human-readable labels for each test dimension */
export const DIMENSION_LABELS: Record<keyof ValidationBattery, string> = {
  convergence: 'Signal Convergence',
  persistence: 'Temporal Persistence',
  geographic: 'Geographic Coherence',
  semantic: 'Semantic Stability',
  noise: 'Noise-to-Signal',
  intent: 'Intent Depth',
  causal: 'Causal Position',
  ethics: 'Ethical Safety',
  falsePositive: 'Falsification',
  readiness: 'Claim Readiness',
};

/** Short descriptions for each test */
export const DIMENSION_DESCRIPTIONS: Record<keyof ValidationBattery, string> = {
  convergence: 'Independent signal classes supporting this trend',
  persistence: 'Survival beyond novelty across rolling windows',
  geographic: 'Spatial diffusion pattern and regional coherence',
  semantic: 'Meaning consistency over time',
  noise: 'Structured interest vs background chatter',
  intent: 'Engagement motivation and commitment depth',
  causal: 'Leading, concurrent, or downstream indicator',
  ethics: 'Data provenance, consent, and activation safety',
  falsePositive: 'Active falsification and historical analog testing',
  readiness: 'Synthesized readiness to inform design or spend',
};

/** State display names (human-readable) */
export const STATE_DISPLAY: Record<string, string> = {
  // Convergence
  'convergent': 'Convergent',
  'single-source': 'Single-source',
  'observational': 'Observational',
  // Persistence
  'persistent': 'Persistent',
  'volatile': 'Volatile',
  'peaking': 'Peaking',
  'decaying': 'Decaying',
  // Geographic
  'diffusing': 'Diffusing',
  'contained': 'Contained',
  'fragmented': 'Fragmented',
  'region-specific': 'Region-specific',
  // Semantic
  'stable': 'Stable meaning',
  'evolving': 'Evolving meaning',
  'fracturing': 'Fracturing meaning',
  // Noise
  'structured': 'Structured',
  'noisy': 'Noisy',
  'speculative': 'Speculative',
  // Intent
  'low-commitment': 'Low-commitment',
  'decision-forming': 'Decision-forming',
  'identity-anchored': 'Identity-anchored',
  // Causal
  'leading': 'Leading',
  'concurrent': 'Concurrent',
  'reactive': 'Reactive',
  // Ethics
  'safe-to-activate': 'Safe to activate',
  'observe-only': 'Observe only',
  'restricted': 'Restricted',
  // False positive
  'robust': 'Robust',
  'unproven': 'Unproven',
  'likely-false-positive': 'Likely false positive',
  // Readiness
  'too-early': 'Too early',
  'forming': 'Forming',
  'defensible': 'Defensible',
  'ready-to-design-against': 'Ready to design against',
};

/** Signal class for state coloring: strong/forming/weak */
export type StateClass = 'strong' | 'forming' | 'weak';

export const STATE_CLASSES: Record<string, StateClass> = {
  // Convergence
  'convergent': 'strong', 'single-source': 'weak', 'observational': 'forming',
  // Persistence
  'persistent': 'strong', 'volatile': 'weak', 'peaking': 'forming', 'decaying': 'weak',
  // Geographic
  'diffusing': 'strong', 'contained': 'forming', 'fragmented': 'weak', 'region-specific': 'forming',
  // Semantic
  'stable': 'strong', 'evolving': 'forming', 'fracturing': 'weak',
  // Noise
  'structured': 'strong', 'noisy': 'weak', 'speculative': 'forming',
  // Intent
  'low-commitment': 'weak', 'decision-forming': 'forming', 'identity-anchored': 'strong',
  // Causal
  'leading': 'strong', 'concurrent': 'forming', 'reactive': 'weak',
  // Ethics
  'safe-to-activate': 'strong', 'observe-only': 'forming', 'restricted': 'weak',
  // False positive
  'robust': 'strong', 'unproven': 'forming', 'likely-false-positive': 'weak',
  // Readiness
  'too-early': 'weak', 'forming': 'forming', 'defensible': 'strong', 'ready-to-design-against': 'strong',
};


// ─── EVIDENCE STRUCTURES ─────────────────────────────────────────
//
// Each dimension produces not just a state but evidence —
// the artifacts that explain WHY the state is what it is.
// This is what people drill into when they want to understand
// the reasoning behind a validation judgment.

export interface SignalSource {
  type: 'search' | 'social' | 'commerce' | 'media';
  label: string;
  detected: boolean;
  detail: string;
}

export interface ConvergenceEvidence {
  sources: SignalSource[];
  reasoning: string;
}

export interface TemporalWindow {
  period: 'short' | 'mid' | 'long';
  label: string;
  state: 'present' | 'strengthening' | 'fading' | 'absent';
}

export interface PersistenceEvidence {
  windows: TemporalWindow[];
  pattern: string;
  reasoning: string;
}

export interface RegionSignal {
  region: string;
  state: 'active' | 'emerging' | 'absent';
  lag?: string;  // e.g., "+3 months" for diffusion lag
}

export interface GeographicEvidence {
  regions: RegionSignal[];
  diffusionPattern: string;
  reasoning: string;
}

export interface SemanticEvidence {
  centroidDrift: 'minimal' | 'moderate' | 'significant';
  meanings: { label: string; share: string }[];  // e.g., [{label: "health-conscious", share: "62%"}]
  reasoning: string;
}

export interface NoiseEvidence {
  querySpecificity: 'high' | 'moderate' | 'low';
  longTailRatio: string;   // e.g., "72% long-tail"
  repeatIntent: string;    // e.g., "41% return queries"
  reasoning: string;
}

export interface IntentBreakdown {
  type: 'exploratory' | 'comparative' | 'transactional' | 'identity';
  label: string;
  share: string;  // e.g., "34%"
}

export interface IntentEvidence {
  breakdown: IntentBreakdown[];
  dominantSignal: string;
  reasoning: string;
}

export interface CausalEvidence {
  position: string;       // e.g., "Emerges 4–6 months before commerce signals"
  precursors: string[];   // related categories that preceded or followed
  reasoning: string;
}

export interface EthicsEvidence {
  consentClass: 'public-aggregated' | 'public-individual' | 'semi-private' | 'sensitive';
  sensitiveDomains: string[];  // e.g., ["minors", "health", "identity"]
  restrictions: string[];      // specific restrictions if any
  reasoning: string;
}

export interface FalsePositiveEvidence {
  historicalAnalogs: { name: string; outcome: 'confirmed' | 'failed' | 'inconclusive'; year: string }[];
  seasonalityCheck: 'passed' | 'flagged' | 'inconclusive';
  nullHypothesis: string;
  reasoning: string;
}

/** Complete evidence package for a cluster's validation battery */
export interface ValidationEvidence {
  convergence: ConvergenceEvidence;
  persistence: PersistenceEvidence;
  geographic: GeographicEvidence;
  semantic: SemanticEvidence;
  noise: NoiseEvidence;
  intent: IntentEvidence;
  causal: CausalEvidence;
  ethics: EthicsEvidence;
  falsePositive: FalsePositiveEvidence;
}


// ─── SYNTHETIC VALIDATION STATE GENERATION ───────────────────────
//
// Derives plausible validation states from editorial properties.
// This is replaced by real validation pipeline in production.
//
// The derivation is editorially consistent:
//   - cluster weight drives structural strength
//   - trend drives temporal dynamics
//   - category character drives systemic properties
//
// The result is defensible synthetic intelligence, not random noise.

interface ClusterValidationInput {
  name: string;
  weight: number;
  trend: 'rising' | 'stable' | 'fading' | 'emerging';
  categoryCharacter: CategoryDefinition['character'];
  categoryMass: number;
}

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

function deriveValidation(input: ClusterValidationInput, rand: () => number): ValidationBattery {
  const { weight, trend, categoryCharacter, categoryMass } = input;

  // ── 1. CONVERGENCE ────────────────────────────────────────
  // High weight + dominant/entrenched character → convergent
  // Low weight or peripheral → single-source or observational
  let convergence: ConvergenceState;
  if (weight > 0.7 && categoryMass > 0.6) {
    convergence = 'convergent';
  } else if (weight > 0.45 && (trend === 'rising' || trend === 'stable')) {
    convergence = rand() > 0.4 ? 'convergent' : 'observational';
  } else {
    convergence = rand() > 0.6 ? 'observational' : 'single-source';
  }

  // ── 2. PERSISTENCE ────────────────────────────────────────
  // Trend is the primary driver here
  let persistence: PersistenceState;
  if (trend === 'stable' && weight > 0.5) {
    persistence = 'persistent';
  } else if (trend === 'rising') {
    persistence = weight > 0.7 ? 'persistent' : 'peaking';
  } else if (trend === 'fading') {
    persistence = 'decaying';
  } else if (trend === 'emerging') {
    persistence = rand() > 0.5 ? 'volatile' : 'peaking';
  } else {
    persistence = 'volatile';
  }

  // ── 3. GEOGRAPHIC ─────────────────────────────────────────
  // Dominant and entrenched → diffusing or contained (broad reach)
  // Fragile and peripheral → fragmented or region-specific
  let geographic: GeographicState;
  if (categoryCharacter === 'dominant' || (categoryCharacter === 'entrenched' && weight > 0.6)) {
    geographic = weight > 0.7 ? 'diffusing' : 'contained';
  } else if (categoryCharacter === 'emerging') {
    geographic = rand() > 0.5 ? 'diffusing' : 'region-specific';
  } else if (categoryCharacter === 'fragile') {
    geographic = rand() > 0.4 ? 'fragmented' : 'region-specific';
  } else if (categoryCharacter === 'diffuse') {
    geographic = 'fragmented';
  } else {
    geographic = rand() > 0.5 ? 'contained' : 'region-specific';
  }

  // ── 4. SEMANTIC STABILITY ─────────────────────────────────
  // Stable trends with high weight → stable meaning
  // Emerging and fading → evolving or fracturing
  let semantic: SemanticState;
  if (trend === 'stable' && weight > 0.5) {
    semantic = 'stable';
  } else if (trend === 'rising' && weight > 0.6) {
    semantic = rand() > 0.3 ? 'stable' : 'evolving';
  } else if (trend === 'emerging') {
    semantic = rand() > 0.6 ? 'evolving' : 'fracturing';
  } else if (trend === 'fading') {
    semantic = rand() > 0.5 ? 'evolving' : 'fracturing';
  } else {
    semantic = 'evolving';
  }

  // ── 5. NOISE-TO-SIGNAL ────────────────────────────────────
  // High weight + convergent → structured
  // Low weight → noisy or speculative
  let noise: NoiseState;
  if (convergence === 'convergent' && weight > 0.6) {
    noise = 'structured';
  } else if (weight > 0.5 && trend !== 'fading') {
    noise = rand() > 0.4 ? 'structured' : 'speculative';
  } else if (trend === 'emerging' || weight < 0.4) {
    noise = rand() > 0.5 ? 'speculative' : 'noisy';
  } else {
    noise = 'noisy';
  }

  // ── 6. INTENT DEPTH ───────────────────────────────────────
  // Entrenched/dominant with high weight → identity-anchored or decision-forming
  // Emerging/peripheral → low-commitment or decision-forming
  let intent: IntentState;
  if ((categoryCharacter === 'entrenched' || categoryCharacter === 'dominant') && weight > 0.7) {
    intent = rand() > 0.4 ? 'identity-anchored' : 'decision-forming';
  } else if (weight > 0.55 && trend === 'rising') {
    intent = 'decision-forming';
  } else if (trend === 'emerging' || weight < 0.45) {
    intent = rand() > 0.7 ? 'decision-forming' : 'low-commitment';
  } else {
    intent = 'decision-forming';
  }

  // ── 7. CAUSAL POSITION ────────────────────────────────────
  // Rising trends with emerging character → leading
  // Stable/entrenched → concurrent
  // Fading → reactive
  let causal: CausalState;
  if (trend === 'rising' && (categoryCharacter === 'emerging' || categoryCharacter === 'dominant')) {
    causal = weight > 0.6 ? 'leading' : 'concurrent';
  } else if (trend === 'stable') {
    causal = 'concurrent';
  } else if (trend === 'fading') {
    causal = 'reactive';
  } else if (trend === 'emerging') {
    causal = rand() > 0.5 ? 'leading' : 'concurrent';
  } else {
    causal = 'concurrent';
  }

  // ── 8. ETHICAL SAFETY ─────────────────────────────────────
  // Most beverage industry data is safe; demographic-sensitive clusters need care
  let ethics: EthicsState;
  if (categoryCharacter === 'fragile') {
    // Demographics & Markets — requires care
    ethics = weight > 0.6 ? 'observe-only' : 'restricted';
  } else if (input.name.toLowerCase().includes('gen z') ||
             input.name.toLowerCase().includes('minor') ||
             input.name.toLowerCase().includes('youth')) {
    ethics = 'restricted';
  } else {
    ethics = 'safe-to-activate';
  }

  // ── 9. FALSE POSITIVE STRESS TEST ─────────────────────────
  // High weight + persistent + convergent → robust
  // Low weight or volatile → unproven
  let falsePositive: FalsePositiveState;
  if (persistence === 'persistent' && convergence === 'convergent' && weight > 0.6) {
    falsePositive = 'robust';
  } else if (persistence === 'decaying' || noise === 'noisy') {
    falsePositive = rand() > 0.5 ? 'unproven' : 'likely-false-positive';
  } else if (weight > 0.5 && trend !== 'fading') {
    falsePositive = rand() > 0.3 ? 'robust' : 'unproven';
  } else {
    falsePositive = 'unproven';
  }

  // ── 10. CLAIM READINESS ───────────────────────────────────
  // Synthesizes everything. Rules-based, not ML-scored.
  let readiness: ReadinessState;
  const strongCount = [convergence, persistence, noise, falsePositive].filter(
    s => STATE_CLASSES[s] === 'strong',
  ).length;

  if (strongCount >= 3 && ethics === 'safe-to-activate') {
    readiness = weight > 0.8 ? 'ready-to-design-against' : 'defensible';
  } else if (strongCount >= 2 && ethics !== 'restricted') {
    readiness = 'defensible';
  } else if (persistence === 'volatile' || falsePositive === 'likely-false-positive') {
    readiness = 'too-early';
  } else {
    readiness = 'forming';
  }

  return {
    convergence,
    persistence,
    geographic,
    semantic,
    noise,
    intent,
    causal,
    ethics,
    falsePositive,
    readiness,
  };
}


// ─── GENERATE ALL CLUSTER VALIDATIONS ────────────────────────────

export interface ClusterValidation {
  clusterId: string;
  clusterName: string;
  category: string;
  battery: ValidationBattery;
  evidence: ValidationEvidence;
}

/**
 * Derive synthetic evidence for each dimension.
 * In production, this comes from the actual validation pipeline.
 * For development, we generate editorially plausible evidence
 * consistent with the cluster's properties and battery state.
 */
function deriveEvidence(
  input: ClusterValidationInput,
  battery: ValidationBattery,
  rand: () => number,
): ValidationEvidence {
  const { name, weight, trend, categoryCharacter } = input;
  const lowerName = name.toLowerCase();

  // ── CONVERGENCE EVIDENCE ──────────────────────────────────
  const searchDetected = weight > 0.4 || trend === 'rising';
  const socialDetected = weight > 0.5 || lowerName.includes('trend') || lowerName.includes('movement');
  const commerceDetected = weight > 0.6 && (categoryCharacter === 'dominant' || categoryCharacter === 'entrenched');
  const mediaDetected = weight > 0.55 && trend !== 'emerging';

  const convergenceEvidence: ConvergenceEvidence = {
    sources: [
      {
        type: 'search', label: 'Search Behavior', detected: searchDetected,
        detail: searchDetected
          ? `Query volume for "${name}" terms shows ${trend === 'rising' ? 'sustained growth' : 'consistent presence'} across search engines`
          : `Minimal search activity detected for related terms`,
      },
      {
        type: 'social', label: 'Social Discourse', detected: socialDetected,
        detail: socialDetected
          ? `Active discussion threads across Reddit, Twitter/X, and industry forums. ${weight > 0.7 ? 'Cross-platform consistency confirmed.' : 'Platform-specific pockets observed.'}`
          : `Limited organic social conversation. Mostly brand-initiated content.`,
      },
      {
        type: 'commerce', label: 'Commerce Indicators', detected: commerceDetected,
        detail: commerceDetected
          ? `SKU proliferation detected. ${weight > 0.8 ? 'Major retailer shelf-space expansion confirmed.' : 'DTC and specialty channels showing traction.'}`
          : `Commerce signals below threshold. No significant retail shift detected yet.`,
      },
      {
        type: 'media', label: 'Media / Publishing', detected: mediaDetected,
        detail: mediaDetected
          ? `Trade publications and mainstream coverage at ${weight > 0.7 ? 'high' : 'moderate'} velocity. ${trend === 'rising' ? 'Accelerating editorial interest.' : 'Steady coverage.'}`
          : `Sparse media coverage. Pre-editorial phase.`,
      },
    ],
    reasoning: battery.convergence === 'convergent'
      ? `Signal is reinforced across ${[searchDetected, socialDetected, commerceDetected, mediaDetected].filter(Boolean).length} independent classes. Not dependent on any single source.`
      : battery.convergence === 'single-source'
        ? `Signal originates primarily from one channel. Insufficient cross-validation to confirm structural reality.`
        : `Signal is observable but lacks the cross-source reinforcement needed for structural confirmation.`,
  };

  // ── PERSISTENCE EVIDENCE ──────────────────────────────────
  const shortPresent = trend !== 'fading';
  const midPresent = trend === 'stable' || (trend === 'rising' && weight > 0.5);
  const longPresent = trend === 'stable' && weight > 0.6;

  const persistenceEvidence: PersistenceEvidence = {
    windows: [
      {
        period: 'short', label: 'Days – Weeks',
        state: shortPresent ? (trend === 'rising' ? 'strengthening' : 'present') : 'fading',
      },
      {
        period: 'mid', label: 'Months',
        state: midPresent ? (trend === 'rising' ? 'strengthening' : 'present') : (trend === 'fading' ? 'fading' : 'absent'),
      },
      {
        period: 'long', label: 'Quarters',
        state: longPresent ? 'present' : (trend === 'emerging' ? 'absent' : (trend === 'fading' ? 'fading' : 'absent')),
      },
    ],
    pattern: battery.persistence === 'persistent' ? 'Reinforcement across all windows'
      : battery.persistence === 'peaking' ? 'Strong short-term, uncertain long-term'
      : battery.persistence === 'decaying' ? 'Fading across progressive windows'
      : 'Inconsistent across time horizons',
    reasoning: battery.persistence === 'persistent'
      ? `Signal has survived multiple novelty cycles. Present across short, mid, and long windows with no decay pattern.`
      : battery.persistence === 'peaking'
        ? `Strong short-term presence but insufficient history to confirm structural persistence. Could be a moment, not yet proven as a trend.`
        : battery.persistence === 'decaying'
          ? `Signal shows progressive weakening across time windows. Short-term residual activity but long-term trajectory is declining.`
          : `Signal appears and disappears unpredictably. No consistent temporal pattern established.`,
  };

  // ── GEOGRAPHIC EVIDENCE ───────────────────────────────────
  const regions: RegionSignal[] = [];
  if (categoryCharacter === 'dominant' || weight > 0.7) {
    regions.push({ region: 'US Northeast', state: 'active' });
    regions.push({ region: 'US West Coast', state: 'active' });
    regions.push({ region: 'US Southeast', state: weight > 0.8 ? 'active' : 'emerging', lag: '+2 months' });
    regions.push({ region: 'US Midwest', state: weight > 0.75 ? 'active' : 'emerging', lag: '+4 months' });
    regions.push({ region: 'UK / Europe', state: weight > 0.85 ? 'emerging' : 'absent', lag: '+6 months' });
  } else if (categoryCharacter === 'emerging') {
    regions.push({ region: 'US West Coast', state: 'active' });
    regions.push({ region: 'US Northeast', state: 'emerging', lag: '+2 months' });
    regions.push({ region: 'US Urban Centers', state: 'emerging' });
    regions.push({ region: 'US Midwest', state: 'absent' });
  } else {
    regions.push({ region: 'US Coastal Metro', state: rand() > 0.5 ? 'active' : 'emerging' });
    regions.push({ region: 'US Secondary Markets', state: rand() > 0.6 ? 'emerging' : 'absent' });
    regions.push({ region: 'US Rural', state: 'absent' });
  }

  const geographicEvidence: GeographicEvidence = {
    regions,
    diffusionPattern: battery.geographic === 'diffusing' ? 'Outward spread with measurable lag times'
      : battery.geographic === 'contained' ? 'Active in multiple regions but not spreading'
      : battery.geographic === 'fragmented' ? 'Scattered pockets with no coherent pattern'
      : 'Concentrated in specific regions with no outward movement',
    reasoning: battery.geographic === 'diffusing'
      ? `Signal shows clear center-out diffusion with measurable regional lag. Pattern consistent with genuine cultural adoption.`
      : battery.geographic === 'contained'
        ? `Signal is present in multiple markets but showing no evidence of active spread. May have reached natural geographic limits.`
        : battery.geographic === 'fragmented'
          ? `Isolated pockets of activity with no coherent spatial pattern. Likely reflects local conditions rather than a unified phenomenon.`
          : `Signal is confined to a specific regional context. May not generalize. Treat as local intelligence.`,
  };

  // ── SEMANTIC EVIDENCE ─────────────────────────────────────
  const meanings: { label: string; share: string }[] = [];
  if (battery.semantic === 'stable') {
    meanings.push({ label: 'Primary meaning cluster', share: '78%' });
    meanings.push({ label: 'Secondary interpretation', share: '22%' });
  } else if (battery.semantic === 'evolving') {
    meanings.push({ label: 'Original meaning', share: '51%' });
    meanings.push({ label: 'Emerging interpretation', share: '35%' });
    meanings.push({ label: 'Peripheral usage', share: '14%' });
  } else {
    meanings.push({ label: 'Meaning A', share: '38%' });
    meanings.push({ label: 'Meaning B', share: '33%' });
    meanings.push({ label: 'Meaning C', share: '29%' });
  }

  const semanticEvidence: SemanticEvidence = {
    centroidDrift: battery.semantic === 'stable' ? 'minimal' : battery.semantic === 'evolving' ? 'moderate' : 'significant',
    meanings,
    reasoning: battery.semantic === 'stable'
      ? `Embedding clusters remain tight over time. The term "${name}" carries a consistent meaning across contexts.`
      : battery.semantic === 'evolving'
        ? `Meaning is shifting. The term is being used in new contexts that weren't present 6 months ago. Monitor for bifurcation.`
        : `Multiple competing meanings detected. The same term is being used to describe fundamentally different phenomena. High risk of false continuity.`,
  };

  // ── NOISE EVIDENCE ────────────────────────────────────────
  const noiseEvidence: NoiseEvidence = {
    querySpecificity: battery.noise === 'structured' ? 'high' : battery.noise === 'speculative' ? 'moderate' : 'low',
    longTailRatio: battery.noise === 'structured' ? `${65 + Math.floor(rand() * 20)}% long-tail`
      : battery.noise === 'speculative' ? `${40 + Math.floor(rand() * 15)}% long-tail`
      : `${20 + Math.floor(rand() * 15)}% long-tail`,
    repeatIntent: battery.noise === 'structured' ? `${35 + Math.floor(rand() * 20)}% return queries`
      : `${10 + Math.floor(rand() * 15)}% return queries`,
    reasoning: battery.noise === 'structured'
      ? `High proportion of specific, long-tail queries with repeat intent. People are researching, not just curious.`
      : battery.noise === 'speculative'
        ? `Mix of structured queries and general curiosity. Signal is forming but not yet differentiated from background interest.`
        : `Dominated by head terms and one-off queries. Pattern resembles meme-driven spikes more than structured interest.`,
  };

  // ── INTENT EVIDENCE ───────────────────────────────────────
  const breakdown: IntentBreakdown[] = [];
  if (battery.intent === 'identity-anchored') {
    breakdown.push({ type: 'identity', label: 'Identity-based', share: `${30 + Math.floor(rand() * 15)}%` });
    breakdown.push({ type: 'transactional', label: 'Transactional', share: `${25 + Math.floor(rand() * 10)}%` });
    breakdown.push({ type: 'comparative', label: 'Comparative', share: `${15 + Math.floor(rand() * 10)}%` });
    breakdown.push({ type: 'exploratory', label: 'Exploratory', share: `${10 + Math.floor(rand() * 10)}%` });
  } else if (battery.intent === 'decision-forming') {
    breakdown.push({ type: 'comparative', label: 'Comparative', share: `${30 + Math.floor(rand() * 15)}%` });
    breakdown.push({ type: 'transactional', label: 'Transactional', share: `${20 + Math.floor(rand() * 15)}%` });
    breakdown.push({ type: 'exploratory', label: 'Exploratory', share: `${20 + Math.floor(rand() * 10)}%` });
    breakdown.push({ type: 'identity', label: 'Identity-based', share: `${8 + Math.floor(rand() * 8)}%` });
  } else {
    breakdown.push({ type: 'exploratory', label: 'Exploratory', share: `${45 + Math.floor(rand() * 15)}%` });
    breakdown.push({ type: 'comparative', label: 'Comparative', share: `${15 + Math.floor(rand() * 10)}%` });
    breakdown.push({ type: 'transactional', label: 'Transactional', share: `${5 + Math.floor(rand() * 8)}%` });
    breakdown.push({ type: 'identity', label: 'Identity-based', share: `${3 + Math.floor(rand() * 5)}%` });
  }

  const intentEvidence: IntentEvidence = {
    breakdown,
    dominantSignal: battery.intent === 'identity-anchored' ? '"I am" and "people like me" queries dominate'
      : battery.intent === 'decision-forming' ? '"best", "vs", "review" queries indicate active evaluation'
      : '"what is" and curiosity-driven queries suggest early-stage awareness',
    reasoning: battery.intent === 'identity-anchored'
      ? `Identity signals are the most predictive. People are self-selecting into this category, not just browsing.`
      : battery.intent === 'decision-forming'
        ? `Active comparison and evaluation behavior. People are making decisions, which means they've already passed the awareness phase.`
        : `Mostly curiosity-driven engagement. People are discovering, not deciding. Signal has potential but hasn't converted to commitment.`,
  };

  // ── CAUSAL EVIDENCE ───────────────────────────────────────
  const precursors: string[] = [];
  if (battery.causal === 'leading') {
    precursors.push(`Emerged before adjacent commerce signals`);
    precursors.push(`Pattern matches early-adoption cycle for ${categoryCharacter} categories`);
  } else if (battery.causal === 'concurrent') {
    precursors.push(`Tracks alongside related category movements`);
    precursors.push(`No clear lead/lag relationship established`);
  } else {
    precursors.push(`Appears after upstream signals in adjacent categories`);
    precursors.push(`Likely a downstream effect, not an independent force`);
  }

  const causalEvidence: CausalEvidence = {
    position: battery.causal === 'leading'
      ? `Emerges 4–8 months before commerce and media signals in adjacent categories`
      : battery.causal === 'concurrent'
        ? `Moves in parallel with related signals. No clear temporal advantage.`
        : `Follows upstream movements by 3–6 months. Likely a consequence, not a cause.`,
    precursors,
    reasoning: battery.causal === 'leading'
      ? `Timing analysis shows this signal consistently precedes related downstream activity. Safe to treat as a leading indicator for planning.`
      : battery.causal === 'concurrent'
        ? `No clear lead/lag relationship. The signal may be part of a broader simultaneous shift rather than an independent force.`
        : `Signal appears to be a downstream effect of other forces. Acting on it directly may be addressing a symptom rather than a cause.`,
  };

  // ── ETHICS EVIDENCE ───────────────────────────────────────
  const sensitiveDomains: string[] = [];
  const restrictions: string[] = [];
  if (categoryCharacter === 'fragile') {
    sensitiveDomains.push('demographic identity');
    sensitiveDomains.push('cultural affiliation');
    restrictions.push('Aggregate only — no individual targeting');
    restrictions.push('Require cultural consultation before activation');
  }
  if (lowerName.includes('gen z') || lowerName.includes('youth')) {
    sensitiveDomains.push('minors');
    restrictions.push('Age-gated activation only');
    restrictions.push('No behavioral targeting of under-21 cohorts');
  }

  const ethicsEvidence: EthicsEvidence = {
    consentClass: categoryCharacter === 'fragile' ? 'semi-private'
      : sensitiveDomains.length > 0 ? 'sensitive'
      : 'public-aggregated',
    sensitiveDomains,
    restrictions,
    reasoning: battery.ethics === 'safe-to-activate'
      ? `Data is publicly available, aggregated, and non-extractive. No consent or sensitivity concerns identified.`
      : battery.ethics === 'observe-only'
        ? `Data involves demographic or culturally sensitive dimensions. Safe to observe and report, but activation requires additional review.`
        : `Signal involves sensitive domains. Downstream activation is restricted until ethical review is completed.`,
  };

  // ── FALSE POSITIVE EVIDENCE ───────────────────────────────
  const analogs: { name: string; outcome: 'confirmed' | 'failed' | 'inconclusive'; year: string }[] = [];
  if (battery.falsePositive === 'robust') {
    analogs.push({ name: `${name} (prior cycle)`, outcome: 'confirmed', year: '2021' });
    analogs.push({ name: 'Comparable category surge', outcome: 'confirmed', year: '2019' });
  } else if (battery.falsePositive === 'unproven') {
    analogs.push({ name: 'Similar emerging signal', outcome: 'inconclusive', year: '2022' });
    if (rand() > 0.5) analogs.push({ name: 'Adjacent category false start', outcome: 'failed', year: '2020' });
  } else {
    analogs.push({ name: 'Comparable hype cycle', outcome: 'failed', year: '2021' });
    analogs.push({ name: 'Seasonal pattern match', outcome: 'failed', year: '2023' });
  }

  const falsePositiveEvidence: FalsePositiveEvidence = {
    historicalAnalogs: analogs,
    seasonalityCheck: battery.falsePositive === 'robust' ? 'passed'
      : battery.falsePositive === 'likely-false-positive' ? 'flagged' : 'inconclusive',
    nullHypothesis: battery.falsePositive === 'robust'
      ? `Null hypothesis (baseline noise) rejected. Signal exceeds seasonal and random variation thresholds.`
      : battery.falsePositive === 'unproven'
        ? `Null hypothesis cannot yet be rejected. Signal is within range of normal variation for this category.`
        : `Signal closely matches known false-positive patterns. Historical analogs suggest this may not survive.`,
    reasoning: battery.falsePositive === 'robust'
      ? `Active falsification attempted and failed. Historical analogs confirm. Seasonality check passed. Signal survives stress testing.`
      : battery.falsePositive === 'unproven'
        ? `Insufficient evidence to either confirm or disprove. More temporal data needed before falsification can be conclusive.`
        : `Historical analogs show similar patterns that did not persist. Seasonality flagged. Treat with caution.`,
  };

  return {
    convergence: convergenceEvidence,
    persistence: persistenceEvidence,
    geographic: geographicEvidence,
    semantic: semanticEvidence,
    noise: noiseEvidence,
    intent: intentEvidence,
    causal: causalEvidence,
    ethics: ethicsEvidence,
    falsePositive: falsePositiveEvidence,
  };
}

/**
 * Generate validation batteries for all main clusters.
 * Seeded for reproducibility — same field, same validation states.
 */
export function generateValidationBatteries(seed: number = 77): Map<string, ClusterValidation> {
  const rand = seededRandom(seed);
  const results = new Map<string, ClusterValidation>();

  CATEGORIES.forEach((category, catIdx) => {
    category.clusters.forEach((cluster, clusterIdx) => {
      const id = `cluster-${catIdx}-${clusterIdx}`;

      const input: ClusterValidationInput = {
        name: cluster.name,
        weight: cluster.weight,
        trend: cluster.trend,
        categoryCharacter: category.character,
        categoryMass: category.gravitationalMass,
      };

      const battery = deriveValidation(input, rand);
      const evidence = deriveEvidence(input, battery, rand);

      results.set(id, {
        clusterId: id,
        clusterName: cluster.name,
        category: category.name,
        battery,
        evidence,
      });
    });
  });

  return results;
}

/**
 * Count strong/forming/weak states in a battery.
 * Used for summary display.
 */
export function countStateClasses(battery: ValidationBattery): { strong: number; forming: number; weak: number } {
  let strong = 0, forming = 0, weak = 0;
  for (const value of Object.values(battery)) {
    const cls = STATE_CLASSES[value];
    if (cls === 'strong') strong++;
    else if (cls === 'forming') forming++;
    else weak++;
  }
  return { strong, forming, weak };
}
